import React from 'react'
import { connect } from 'react-redux'
import moment from 'moment-timezone'
import PropTypes from 'prop-types'
import { TrackingTag } from 'transfix-ui/components/TrackingTag'
import { submitNote } from 'utils/actions/notes'
import {
  updateShipment,
  updateShipmentStatus,
  updateUnresponsiveCount,
} from 'utils/actions/shipments'
import { openDialog, closeDialog } from 'utils/actions/ui'
import { getUserDetails } from 'utils/hooks/useUserDetails'
import { validateCheckin } from 'utils/appointmentHelpers'
import { createManualEta } from 'utils/etaHelpers'
import { formatProps } from 'utils/formatProps'
import { lookupCountryByStateCode } from 'utils/getLocations'
import { getNextStatus } from 'utils/shipmentHelpers'
import TIME_CONSTANTS from 'constants/datetime'
import {
  DIALOG_UPDATE_STATUSES,
  BEFORE_TRANSIT_LABELS,
  KEYED_SHIPMENT_STATUSES,
} from 'constants/shipments'
import {
  showInvalidCallback,
  showOsd,
  updateShipmentDetails,
} from '../../utils/dialogHelperFunctions'
import { Checkin, Osd } from './Checkin'
import { Eta, LocationAndMiles } from './Eta'
import { DialogNotes } from './Notes'

const Tracking = React.memo(({ isDriverTracking, timestamp, timezone }) => (
  <>
    <TrackingTag
      align='left'
      trackingRequired
      isDriverTracking={isDriverTracking}
      timezone={timezone}
      timestamp={timestamp}
      message
    />
  </>
))

export class DialogContent extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      body: '',
      etaDate: '',
      etaLate: false,
      etaTime: '',
      callbackDate: '',
      callbackTime: '',
      checkinDate: '',
      checkinTime: '',
      invalidCheckin: '',
      lateReason: '',
      location: {},
      miles: '',
      osd: false,
      recommendBounceReason: '',
      recommendBounceSelected: false,
      showCallback: '',
      label: this.getLabel(props),
      nextStatus: this.getNextStatus(props),
      currentStop: this.getCurrentStop(props),
      dropOrTrusted: this.dropOrTrusted(props),
      unresponsive: false,
      detention: false,
    }
  }

  componentDidMount() {
    window.addEventListener('reset', closeDialog)
  }

  componentWillUnmount() {
    window.removeEventListener('reset', closeDialog)
  }

  changeNoteHandler = body => this.setState({ body })

  changeMilesHandler = e => this.setState({ [e.target.name]: e.target.value })

  onChangeTime = (value, name) => {
    if (name === 'etaTime') {
      this.setState({ [name]: value }, () => {
        this.setState(prevState => {
          const manualEta = createManualEta(prevState, this.props)
          return { etaLate: !!manualEta.isLate, manualEta: manualEta.formatted }
        })
      })
    } else if (name === 'checkinTime') {
      this.setState({ [name]: value }, () => {
        this.setState(prevState => ({ invalidCheckin: validateCheckin(prevState) }))
      })
    }
  }

  onChangeCheckinDate = ({ date = '' }) => {
    this.setState({ checkinDate: date }, () => {
      this.setState(prevState => ({ invalidCheckin: validateCheckin(prevState) }))
    })
  }

  onChangeEtaDate = ({ date = '' }) =>
    this.setState({ etaDate: date }, () => {
      this.setState(prevState => {
        const manualEta = createManualEta(prevState, this.props)
        return { etaLate: !!manualEta.isLate }
      })
    })

  onChangeCallbackButton = () => {
    this.setState(
      pState => ({ showCallback: !pState.showCallback }),
      () => {
        this.setState(prevState => ({ invalidCallback: showInvalidCallback(prevState) }))
      }
    )
  }

  onChangeCallbackDate = ({ date = '' }) => {
    this.setState({ callbackDate: date }, () => {
      this.setState(prevState => ({ invalidCallback: showInvalidCallback(prevState) }))
    })
  }

  onChangeCallbackTime = value => {
    this.setState({ callbackTime: value }, () => {
      this.setState(prevState => ({ invalidCallback: showInvalidCallback(prevState) }))
    })
  }

  onChangeLocation = ({ city, state }) => {
    this.setState({
      location: { city, state, country: state ? lookupCountryByStateCode(state).code : '' },
    })
  }

  onChangeDetention = e => this.setState({ detention: e.target.checked })

  onChangeUnresponsive = e => {
    e.persist()
    this.setState(prevState => ({
      unresponsive: e.target.checked,
      etaDate: e.target.checked ? null : prevState.etaDate,
      etaTime: e.target.checked ? null : prevState.etaTime,
    }))
  }

  onChangeOsd = e => this.setState({ osd: e.target.checked })

  onChangeLateReason = (_, option) => this.onChangeSelect(option, 'lateReason')

  onChangeRecommendBounceReason = (_, option) =>
    this.onChangeSelect(option, 'recommendBounceReason')

  onChangeSelect = (option, name) => this.setState({ [name]: option.value })

  onSubmitNote = event => {
    event.preventDefault()
    const { body, callbackDate, callbackTime, showCallback } = this.state
    const notesBody = this.createNoteBody(body)
    const { id: userId } = getUserDetails() || {}

    const requestData = {
      body: notesBody,
      createdById: userId,
      noteableId: this.props.shipmentId,
      noteableType: 'Shipment',
    }
    if (showCallback) {
      requestData.callbackDate = moment(callbackDate).format(TIME_CONSTANTS.dateFormat)
      requestData.callbackTime = callbackTime
    }
    this.props.submitNote(requestData)
  }

  getCurrentStop(props) {
    const {
      shipment: {
        status: { currentStopIndex },
        stops,
      },
    } = props
    const currentStop = stops[currentStopIndex]
    return currentStop
  }

  getLabel(props) {
    const {
      shipment: { status },
    } = props
    let label = ''
    if (status) {
      label = getNextStatus(DIALOG_UPDATE_STATUSES, status.state)
      if (label === 'In Transit') label = 'Picked up'
    }
    return label
  }

  getNextStatus(props) {
    const {
      shipment: { status },
    } = props
    let nextStatus = ''
    if (status) {
      nextStatus = getNextStatus(DIALOG_UPDATE_STATUSES, status.state)
    }
    return nextStatus
  }

  checkTrackingUpdate = (statusUpdate, tracking) => statusUpdate === 'Tracking Update' || tracking

  checkRecommendBounce = () =>
    this.setState(prevState => ({ recommendBounceSelected: !prevState.recommendBounceSelected }))

  createNoteBody = () => {
    const notes = []
    const {
      body,
      checkinDate,
      checkinTime,
      detention,
      etaDate,
      etaTime,
      label,
      lateReason,
      location,
      miles,
      nextStatus,
      osd,
      recommendBounceReason,
      unresponsive,
    } = this.state
    const trackingUpdate = this.checkTrackingUpdate(this.props.statusUpdate, this.props.tracking)
    if (unresponsive) {
      notes.push('Driver unresponsive')
      this.setState({ manualEta: '', lateReason: '' })
    }
    if (detention) notes.push('Tracking Update: This load is entering detention time')
    if (recommendBounceReason) notes.push(`Recommend Bounce: ${recommendBounceReason}`)
    if (!trackingUpdate) {
      if (nextStatus !== KEYED_SHIPMENT_STATUSES.dispatched.label) {
        notes.push([`${label} on ${checkinDate} at ${checkinTime}`])
      }
      if (osd) notes.push('OS&D')
      if (nextStatus === KEYED_SHIPMENT_STATUSES.in_transit.label) {
        if (etaDate && etaTime) notes.push(`Delivery ETA: ${etaDate} at ${etaTime}`)
      }
      if (nextStatus === KEYED_SHIPMENT_STATUSES.dispatched.label) {
        if (etaDate && etaTime) notes.push(`Pick Up ETA: ${etaDate} at ${etaTime}`)
      }
    } else if (etaDate) notes.push(`Update ETA: ${etaDate} at ${etaTime}`)
    if (lateReason) notes.push(`Late Reason: ${lateReason}`)
    if (location && location.city && location.state) {
      const { city, country, state } = location
      notes.push(`Current Location: ${city}, ${state}, ${country}`)
    }
    if (miles) notes.push(`Miles to go: ${miles}`)
    if (body) notes.push(body)

    return [...notes].join(' &middot; ')
  }

  dropOrTrusted(props) {
    const {
      shipment: { info },
    } = props
    return info.carrierIsTrusted || info.dropTrailer
  }

  allowSubmit = () => {
    const {
      checkinDate,
      checkinTime,
      detention,
      dropOrTrusted,
      etaDate,
      etaLate,
      etaTime,
      invalidCallback,
      invalidCheckin,
      lateReason,
      nextStatus,
      recommendBounceReason,
      recommendBounceSelected,
      unresponsive,
    } = this.state
    const trackingUpdate = this.checkTrackingUpdate(this.props.statusUpdate, this.props.tracking)
    if (detention) return true
    if (unresponsive) {
      if (recommendBounceSelected) return !!recommendBounceReason
      return true
    }
    if (recommendBounceSelected) return !!recommendBounceReason
    const lateReasonProvided = etaLate ? lateReason : true
    if (nextStatus === KEYED_SHIPMENT_STATUSES.dispatched.label) {
      if (dropOrTrusted) return lateReasonProvided
      return etaDate && etaTime && lateReasonProvided
    }
    if (trackingUpdate) return etaDate && etaTime && lateReasonProvided && !invalidCallback
    const etaTimeProvided = etaDate ? etaTime : true
    return (
      checkinDate &&
      checkinTime &&
      !invalidCheckin &&
      !invalidCallback &&
      etaTimeProvided &&
      lateReasonProvided
    )
  }

  handleSubmit = event => {
    event.preventDefault()
    this.onSubmitNote(event)
    const body = updateShipmentDetails(this.props, this.state)
    const { shipmentId } = this.props
    const { nextStatus, recommendBounceSelected, unresponsive } = this.state
    const trackingUpdate = this.checkTrackingUpdate(this.props.statusUpdate, this.props.tracking)

    if (!trackingUpdate) {
      if (nextStatus === KEYED_SHIPMENT_STATUSES.dispatched.label) {
        this.props.updateShipment(shipmentId, body)
        // the handleChangeStatus should happen in the BE
        this.props.updateShipmentStatus(shipmentId, 'dispatch')
      } else {
        this.props.updateShipment(shipmentId, body)
      }
    } else if (unresponsive) {
      this.props.updateUnresponsiveCount(shipmentId)
      if (recommendBounceSelected) {
        this.props.updateShipment(shipmentId, body)
      }
    } else {
      this.props.updateShipment(shipmentId, body)
    }
    if (this.props.closeDialog) this.props.closeDialog()
  }

  render() {
    const {
      checkinDate,
      checkinTime,
      detention,
      dropOrTrusted,
      etaDate,
      etaLate,
      etaTime,
      invalidCallback,
      invalidCheckin,
      label,
      lateReason,
      location,
      miles,
      nextStatus,
      osd,
      recommendBounceReason,
      recommendBounceSelected,
      unresponsive,
    } = this.state
    const {
      modal,
      shipment: {
        driverLocationTimezone: timezone,
        isDriverTracking,
        latestDriverLocationAt: timestamp,
      },
      statusUpdate,
      tracking,
    } = this.props
    const trackingUpdate = this.checkTrackingUpdate(statusUpdate, tracking)
    const validForm = this.allowSubmit()
    const showingOsd = showOsd(nextStatus) && !trackingUpdate
    const buttonText = trackingUpdate ? 'Update ETA' : label

    return (
      <>
        <form onSubmit={this.handleSubmit}>
          {nextStatus !== KEYED_SHIPMENT_STATUSES.dispatched.label && !trackingUpdate && (
            <Checkin
              label={label}
              nextStatus={nextStatus}
              props={this.props}
              checkinDate={checkinDate}
              checkinTime={checkinTime}
              checkCheckin={this.checkCheckin}
              checkinWarning={invalidCheckin}
              onChangeDate={this.onChangeCheckinDate}
              onChangeTime={this.onChangeTime}
            />
          )}
          {showingOsd && <Osd onChangeOsd={this.onChangeOsd} osd={osd} />}
          {(BEFORE_TRANSIT_LABELS.includes(nextStatus) || trackingUpdate) && (
            <>
              <Eta
                dropOrTrusted={dropOrTrusted}
                label={label}
                nextStatus={nextStatus}
                etaDate={etaDate}
                etaLate={etaLate}
                etaTime={etaTime}
                lateReason={lateReason}
                onChangeDate={this.onChangeEtaDate}
                onChangeTime={this.onChangeTime}
                onChangeLateReason={this.onChangeLateReason}
                trackingUpdate={trackingUpdate}
                onChangeUnresponsive={this.onChangeUnresponsive}
                unresponsive={unresponsive}
                detention={detention}
                onChangeDetention={this.onChangeDetention}
                recommendBounceReason={recommendBounceReason}
                recommendBounceSelected={recommendBounceSelected}
                onChangeRecommendBounceReason={this.onChangeRecommendBounceReason}
                checkRecommendBounce={this.checkRecommendBounce}
              />
              <LocationAndMiles
                location={location}
                miles={miles}
                onChangeMiles={this.changeMilesHandler}
                onChangeLocation={this.onChangeLocation}
              />
            </>
          )}
          {trackingUpdate && (
            <Tracking
              isDriverTracking={isDriverTracking}
              timezone={timezone}
              timestamp={timestamp}
            />
          )}
          <DialogNotes
            onChangeCallback={this.onChangeCallbackButton}
            onChangeCallbackDate={this.onChangeCallbackDate}
            onChangeCallbackTime={this.onChangeCallbackTime}
            onChangeHandler={this.changeNoteHandler}
            invalidCallback={invalidCallback}
          />
          <div className='dialog-footer tr'>
            {modal && (
              <button type='reset' className='btn-text-link' data-test='cancel-button'>
                Cancel
              </button>
            )}
            <button className='btn-primary' data-test='status-update-submit' disabled={!validForm}>
              {buttonText}
            </button>
          </div>
        </form>
      </>
    )
  }
}

Tracking.propTypes = {
  isDriverTracking: PropTypes.bool,
  timezone: PropTypes.string,
  timestamp: PropTypes.string,
}
Tracking.defaultProps = {
  isDriverTracking: false,
  timezone: '',
  timestamp: '',
}

DialogContent.propTypes = {
  shipmentId: PropTypes.number,
  shipment: PropTypes.shape({}),
  closeDialog: PropTypes.func,
  submitNote: PropTypes.func.isRequired,
  updateShipment: PropTypes.func.isRequired,
  updateShipmentStatus: PropTypes.func.isRequired,
  updateUnresponsiveCount: PropTypes.func.isRequired,
  tracking: PropTypes.bool,
  modal: PropTypes.bool,
}
DialogContent.defaultProps = {
  shipmentId: null,
  shipment: {},
  closeDialog: () => {},
  tracking: false,
  modal: true,
}

const mapStateToProps = ({
  sidebar: {
    activeRowId: shipmentId,
    data: { shipment },
  },
  ui: { statusUpdate },
}) => ({
  shipmentId,
  shipment: formatProps(shipment),
  statusUpdate,
})

const mapDispatchToProps = dispatch => ({
  updateShipment: (id, params) => dispatch(updateShipment(id, params)),
  updateShipmentStatus: (id, key) => dispatch(updateShipmentStatus(id, key)),
  updateUnresponsiveCount: id => dispatch(updateUnresponsiveCount(id)),
  openDialog: () => dispatch(openDialog()),
  closeDialog: () => dispatch(closeDialog()),
  submitNote: data => dispatch(submitNote(data)),
})

export const UpdateStatusDialog = connect(mapStateToProps, mapDispatchToProps)(DialogContent)
